// The burn auction's reserve grid and clearing rule, mirrored from PapurrBurnAuction.sol so the app can show a
// seller what its reserve means and whether the epoch would take its sale as things stand. Prices are HYPE
// core wei (8 decimals) per whole PAPURR, exactly as the contract computes them (integer steps).

/** Ticks of the reserve grid: 0 is any price, 1..95 grow by a quarter each (floored) from BASE_TICK_PRICE */
export const TICKS = 96;
export const BASE_TICK_PRICE = 10n;
/** `cutoff` of an epoch that took nothing */
export const NO_TICK = 2 ** 32 - 1;
const PRICE_UNIT = 10n ** 18n;

/** The grid, computed once: TICK_PRICES[k] === PapurrBurnAuction.tickPrice(k) */
export const TICK_PRICES: readonly bigint[] = (() => {
  const out: bigint[] = [0n, BASE_TICK_PRICE];
  for (let k = 2; k < TICKS; k++) out.push((out[k - 1]! * 5n) / 4n);
  return out;
})();

export function tickPrice(tick: number): bigint {
  if (!Number.isInteger(tick) || tick < 0 || tick >= TICKS) throw new RangeError(`tick ${tick}`);
  return TICK_PRICES[tick]!;
}

/** The highest tick whose price is at most `price` (core wei per PAPURR); 0 when none fits */
export function tickAtOrBelow(price: bigint): number {
  let tick = 0;
  for (let k = 1; k < TICKS; k++) if (TICK_PRICES[k]! <= price) tick = k;
  return tick;
}

export type Clearing = {
  /** Highest tick taken; NO_TICK when nothing is */
  cutoff: number;
  /** PAPURR taken, 18 dec */
  cleared: bigint;
  /** The pot over the PAPURR taken, core wei per whole PAPURR; 0n when nothing is taken */
  price: bigint;
};

/**
 * How an epoch clears at `potWei` with `amounts` (PAPURR per tick, 18 dec): ticks from the lowest up, each
 * taken while the pot pays its price for everything taken so far; the first that fails ends it (higher ticks
 * ask more for more). PapurrBurnAuction._clearing, to the wei.
 */
export function clearBook(amounts: readonly bigint[], potWei: bigint): Clearing {
  let cutoff = NO_TICK;
  let taken = 0n;
  if (potWei > 0n) {
    for (let k = 0; k < Math.min(amounts.length, TICKS); k++) {
      const a = amounts[k]!;
      if (a === 0n) continue;
      if (potWei * PRICE_UNIT < TICK_PRICES[k]! * (taken + a)) break;
      taken += a;
      cutoff = k;
    }
  }
  return { cutoff, cleared: taken, price: taken === 0n ? 0n : (potWei * PRICE_UNIT) / taken };
}

/** `clearBook` with one more sale of `amount` at `tick` added to the book */
export function clearWith(amounts: readonly bigint[], potWei: bigint, tick: number, amount: bigint): Clearing {
  const book = Array.from({ length: TICKS }, (_, k) => amounts[k] ?? 0n);
  book[tick] = book[tick]! + amount;
  return clearBook(book, potWei);
}
